import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/infra/utils';
import type { UsDiscoveryMarket } from '@/lib/polymarketUs/marketClient';
import { usOrderReason } from '@/lib/polymarketUs/reason';
import type { UsTicketMarkets } from './ticketAdapter';
import { US_GAME_SPORTS, type UsGameSport } from '@/lib/polymarketUs/sports';

type Sport = UsGameSport | null;
type ListState =
  | { status: 'loading'; markets: UsDiscoveryMarket[]; nextCursor: string | null }
  | { status: 'ready'; markets: UsDiscoveryMarket[]; nextCursor: string | null }
  | { status: 'error'; markets: UsDiscoveryMarket[]; nextCursor: string | null; reason: string };

const BOOK_LABEL: Record<UsDiscoveryMarket['book']['state'], string> = {
  current: 'Book current',
  stale: 'Book stale',
  unavailable: 'Book unavailable',
};

const control = 'border-border rounded border px-2 py-1 text-xs disabled:opacity-50';
const reasonOf = (e: unknown) =>
  e && typeof e === 'object' && 'code' in e && typeof e.code === 'string' && /^(US|RELAY)_[A-Z0-9_]{1,100}$/.test(e.code)
    ? e.code
    : 'US_MARKET_UNAVAILABLE';

export function marketTitle(m: UsDiscoveryMarket): string {
  return m.title ?? m.marketSlug;
}

type Props = {
  markets: UsTicketMarkets;
  selectedSlug: string | null;
  onSelect(slug: string): void;
};

/**
 * Account-scoped game catalogue. Only the latest search or page paints;
 * nothing polls. A row's book state is shown as reported, never upgraded.
 */
export function UsMarketPicker({ markets, selectedSlug, onSelect }: Props) {
  const request = useRef(0);
  const [query, setQuery] = useState('');
  const [sport, setSport] = useState<Sport>(null);
  const [list, setList] = useState<ListState>({ status: 'loading', markets: [], nextCursor: null });

  const load = useCallback(
    async (q: string, s: Sport, after: string | null) => {
      const mine = ++request.current;
      setList((current) => ({ status: 'loading', markets: after ? current.markets : [], nextCursor: current.nextCursor }));
      try {
        const page = await markets.listMarkets({
          ...(q.trim() ? { q: q.trim() } : {}),
          ...(s ? { sport: s } : {}),
          ...(after ? { after } : {}),
        });
        if (mine !== request.current) return;
        setList((current) => ({
          status: 'ready',
          markets: after ? [...current.markets, ...page.markets] : page.markets,
          nextCursor: page.nextCursor,
        }));
      } catch (e) {
        if (mine !== request.current) return;
        setList((current) => ({ status: 'error', markets: current.markets, nextCursor: current.nextCursor, reason: reasonOf(e) }));
      }
    },
    [markets],
  );

  useEffect(() => {
    void load('', null, null);
    const latest = request;
    return () => {
      latest.current++; // nothing in flight may paint after unmount
    };
  }, [load]);

  const chooseSport = (next: Sport) => {
    setSport(next);
    void load(query, next, null);
  };

  return (
    <section aria-label="US markets" className="border-border flex min-w-0 flex-col rounded border p-3" data-testid="us-market-picker">
      <h3 className="text-sm font-semibold">Market</h3>
      <form
        className="mt-2 flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void load(query, sport, null);
        }}
      >
        <input
          type="search"
          aria-label="Search US markets"
          placeholder="Team or market"
          maxLength={120}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="border-border bg-input min-w-0 flex-1 rounded border px-2 py-1 text-xs"
        />
        <button type="submit" className={control}>Search</button>
        <div role="group" aria-label="Sport" className="flex gap-1">
          {([null, ...US_GAME_SPORTS] as const).map((s) => (
            <button
              key={s ?? 'all'}
              type="button"
              aria-pressed={sport === s}
              onClick={() => chooseSport(s)}
              className={cn(control, sport === s && 'bg-muted text-foreground font-semibold')}
            >
              {s ?? 'All'}
            </button>
          ))}
        </div>
      </form>
      {list.status === 'error' && (
        <p role="alert" className="mt-2 break-words text-xs" title={list.reason}>
          Markets unavailable: {usOrderReason(list.reason)}. Kalshi and Polymarket are unaffected.
        </p>
      )}
      <ul className="mt-2 max-h-96 min-h-0 space-y-1 overflow-y-auto pr-1" aria-label="Market results">
        {list.markets.map((m) => {
          const selected = m.marketSlug === selectedSlug;
          return (
            <li key={m.marketSlug}>
              <button
                type="button"
                aria-pressed={selected}
                data-testid="us-market-row"
                onClick={() => onSelect(m.marketSlug)}
                className={cn(
                  'border-border w-full min-w-0 rounded border px-2 py-1.5 text-left text-xs',
                  selected ? 'bg-muted ring-primary ring-1' : 'hover:bg-muted/60',
                )}
              >
                <span className="flex items-baseline justify-between gap-2">
                  <span className="min-w-0 break-words font-medium [overflow-wrap:anywhere]">{marketTitle(m)}</span>
                  <span className="text-muted-foreground shrink-0">{m.sport}</span>
                </span>
                <span className="text-muted-foreground mt-0.5 flex flex-wrap gap-x-3">
                  <span className="break-all font-mono">{m.marketSlug}</span>
                  <span className={cn(m.book.state !== 'current' && 'text-amber-500')}>{BOOK_LABEL[m.book.state]}</span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      {list.status === 'ready' && list.markets.length === 0 && (
        <p className="text-muted-foreground mt-2 text-xs">No NFL or CFB markets match.</p>
      )}
      <div className="mt-2 flex items-center gap-2 text-xs">
        {list.status === 'loading' && <span className="text-muted-foreground">Loading markets…</span>}
        {list.nextCursor && list.status !== 'loading' && (
          <button type="button" className={control} onClick={() => void load(query, sport, list.nextCursor)}>
            Load more
          </button>
        )}
      </div>
    </section>
  );
}
